import React, { useState } from 'react';

import { DateTime } from 'luxon';

import { ORDER_SUBTYPE_TO_NAME } from '@admin/constants/order_subtype';
import { ORDER_TYPE_TO_NAME } from '@admin/constants/order_type';
import { client } from '@admin/libraries/apollo';
import { useOrderLeadTimesQuery } from '@admin/schema';
import { Button } from '@admin/styled/button';
import { Card } from '@admin/styled/card';
import { CardToolbar } from '@admin/styled/card_toolbar';
import { MutedPanel } from '@admin/styled/muted_panel';
import { SectionActions } from '@admin/styled/section_actions';
import { Table } from '@admin/styled/table';
import { TableWrap } from '@admin/styled/table_wrap';

import { OrderLeadTimeEditingType, UpsertModal } from './upsert_modal';

export const OrderLeadTimePanel: React.FC<{
  warehouseID: string;
  editAccess?: boolean;
}> = ({ warehouseID, editAccess }) => {
  const [showUpsertModal, setShowUpsertModal] = useState<boolean>(false);
  const [editingOrderLeadTime, setEditingOrderLeadTime] = useState<OrderLeadTimeEditingType | undefined>(undefined);

  const { data, loading, refetch } = useOrderLeadTimesQuery({
    client,
    variables: { warehouseID: warehouseID },
  });

  const orderLeadTimes = data?.orderLeadTimes ?? [];

  const openCreateModal = () => {
    setEditingOrderLeadTime(undefined);
    setShowUpsertModal(true);
  };

  const openEditModal = (orderLeadTime: OrderLeadTimeEditingType) => {
    setEditingOrderLeadTime(orderLeadTime);
    setShowUpsertModal(true);
  };

  return (
    <>
      <Card>
        {editAccess && (
          <CardToolbar>
            <SectionActions style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button variant="secondary" size="sm" type="button" onClick={openCreateModal}>
                Add lead time
              </Button>
            </SectionActions>
          </CardToolbar>
        )}
        {loading && orderLeadTimes.length === 0 ? (
          <MutedPanel>Loading…</MutedPanel>
        ) : orderLeadTimes.length === 0 ? (
          <MutedPanel>No order lead times are configured yet.</MutedPanel>
        ) : (
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <th>Order type</th>
                  <th>Order subtype</th>
                  <th>Booking lead days required</th>
                  <th>Booking lead day type</th>
                  <th>Booking lead day cutoff time</th>
                  {editAccess && (
                    <>
                      <th>Last updated</th>
                      <th />
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {orderLeadTimes.map((orderLeadTime) => (
                  <tr key={orderLeadTime.id}>
                    <td>{ORDER_TYPE_TO_NAME[orderLeadTime.orderType]}</td>
                    <td>{ORDER_SUBTYPE_TO_NAME[orderLeadTime.orderSubtype]}</td>
                    <td>{orderLeadTime.processingDaysRequired}</td>
                    <td>{orderLeadTime.ignoreNonOperatingDays ? 'Operating days' : 'Calendar days'}</td>
                    <td>
                      {DateTime.fromISO(orderLeadTime.processingDayCutoffTime, { zone: 'UTC' }).toLocaleString(
                        DateTime.TIME_SIMPLE,
                      )}
                    </td>
                    {editAccess && (
                      <>
                        <td>{DateTime.fromISO(orderLeadTime.updatedAt).toLocaleString(DateTime.DATETIME_MED)}</td>
                        <td style={{ textAlign: 'right' }}>
                          <Button
                            variant="secondary"
                            size="sm"
                            type="button"
                            onClick={() =>
                              openEditModal({
                                id: orderLeadTime.id,
                                ignoreNonOperatingDays: orderLeadTime.ignoreNonOperatingDays,
                                orderSubtype: orderLeadTime.orderSubtype,
                                orderType: orderLeadTime.orderType,
                                processingDayCutoffTime: orderLeadTime.processingDayCutoffTime,
                                processingDaysRequired: orderLeadTime.processingDaysRequired,
                              })
                            }
                          >
                            Edit
                          </Button>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrap>
        )}
      </Card>
      {showUpsertModal && (
        <UpsertModal
          orderLeadTime={editingOrderLeadTime}
          existingOrderLeadTimeKinds={orderLeadTimes.map((orderLeadTime) => ({
            orderSubtype: orderLeadTime.orderSubtype,
            orderType: orderLeadTime.orderType,
          }))}
          warehouseID={warehouseID}
          closeModal={() => {
            setEditingOrderLeadTime(undefined);
            setShowUpsertModal(false);
            refetch();
          }}
        />
      )}
    </>
  );
};
